// EditModal.tsx
import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, message } from "antd";
import { useTranslation } from "react-i18next";
import { AnyObject } from "yup";
import UserEditForm from "./Form";
import { useMessageError } from "../../../../hooks/common";
import {
  UserUpdateParams,
  useUserGetById,
  useUserUpdate,
} from "../../../../hooks/api/staff";

interface EditModalProps {
  open: boolean;
  userId?: string;
  onClose: () => void;
  setTableKey?: () => void;
}

export default function EditModal({
  userId,
  open,
  onClose,
  setTableKey,
}: EditModalProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const initialValuesRef = useRef<AnyObject>(null);
  const [isDirty, setIsDirty] = useState(false);

  // autoStart: false — запускаем вручную когда открывается модалка
  const userGetById = useUserGetById();
  const userUpdate = useUserUpdate();

  useEffect(() => {
    if (!open || !userId) return;
    // Запускаем запрос и заполняем форму СВЕЖИМ результатом запроса.
    // Важно: нельзя читать userGetById.data из замыкания — там значение с
    // прошлого рендера (старая роль). Берём данные из resolve fetch().
    userGetById.fetch(undefined, userId).then((userData) => {
      if (userData) {
        const formValues = {
          email: userData.email,
          fullName: userData.fullName,
          address: userData.address,
          phone: userData.phone,
          role: userData.role,
          status: userData.status,
        };

        form.setFieldsValue(formValues);
        initialValuesRef.current = formValues;
        setIsDirty(false);
      }
    });
  }, [open, userId]);

  const handleSubmit = async (values: any) => {
    if (!userId) return;

    const payload: UserUpdateParams = {
      email: values.email,
      fullName: values.fullName,
      address: values.address,
      phone: values.phone,
      status: values.status,
      role: values.role,
    };

    try {
      const res = await userUpdate.fetch(payload, userId);
      if (res) {
        message.success("User has been updated successfully");
        form.resetFields();
        onClose();
        setTableKey?.();
      }
    } catch (error: any) {
      message.error(error?.message);
    }
  };

  useMessageError([userUpdate, userGetById]);

  const handleClose = () => {
    form.resetFields();
    // initialValuesRef.current = null;
    setIsDirty(false);
    onClose();
  };

  const footer = [
    <Button key="cancel" onClick={handleClose}>
      {t("common.cancel")}
    </Button>,
    <Button
      key="save"
      type="primary"
      // loading={userUpdate.loading || userGetById.loading}
      onClick={() => form.submit()}
    >
      Save Changes
    </Button>,
  ];

  return (
    <Modal
      title="Edit User"
      open={open}
      onCancel={handleClose}
      destroyOnHidden
      width={520}
      footer={footer}
      confirmLoading={userGetById.loading}
    >
      <Form
        form={form}
        layout="vertical"
        name="userEditForm"
        onFinish={handleSubmit}
        onValuesChange={(_, allValues) => {
          const isChanged =
            JSON.stringify(allValues) !==
            JSON.stringify(initialValuesRef.current);
          setIsDirty(isChanged);
        }}
      >
        <UserEditForm />
      </Form>
    </Modal>
  );
}
